import { useState, useMemo, useEffect } from "react";
import {
    Autocomplete,
    Box,
    CardContent,
    Divider,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from "@mui/material";
import { useTranslation } from "shared/hooks/useTranslation";
import MoneyInput from "shared/ui/MoneyInput";
import { useMarketQuotes } from "features/market";
import {
    flattenQuotesForAddDialog,
    type FlattenedQuoteForAdd,
} from "features/market";
import { PORTFOLIO_PANEL_FILTERS, CRYPTO_OPTIONS, type PortfolioPanelFilter } from "features/market";
import type { AddOperationPayload, PositionWithAsset } from "types/Portfolio";
import { DialogCard } from "shared/ui/ComposedCards";
import FormDialog from "shared/ui/FormDialog";
import FormActions from "shared/ui/FormActions";
import ConfirmDialog from "shared/ui/ConfirmDialog";
import { FORM_FIELD_GAP } from "shared/ui/formConstants";
import { useMarketQuotesContext } from "features/market";
import { useOfflineDisabled } from "shared/hooks/useOfflineDisabled";
import { useFinancialVisibility, useUserSession } from "shared/contexts/UserContext";
import { formatCurrencyAmountOrMasked } from "shared/money/balances";
import { LiquidityPoolSelector } from "features/wallet";
import { WalletEmptyHint } from "features/wallet";
import { toast } from "sonner";
import { useWallet } from "features/wallet";
import { useFormDraft } from "shared/hooks/useFormDraft";

type SourceType = "market" | "custom";

export interface AddOperationInitialValues {
    side?: "buy" | "sell" | "amortization";
    type?: SourceType;
    symbol?: string;
    name?: string;
    marketPanel?: PortfolioPanelFilter;
    quantity?: number;
    price?: number;
    date?: string;
    currency?: string;
}

interface AddOperationCardProps {
    onSuccess: () => void;
    addOperation: (payload: AddOperationPayload) => Promise<{ success: boolean; error?: string }>;
    positions: PositionWithAsset[];
    onCreateWalletTransaction: (
        payload: AddOperationPayload,
        liquidityPoolId: string
    ) => Promise<{ success: boolean; error?: string }>;
    variant?: "floating" | "dialog";
    open?: boolean;
    onClose?: () => void;
    initialValues?: AddOperationInitialValues | null;
}

export default function AddOperationCard({
    onSuccess,
    addOperation,
    positions,
    onCreateWalletTransaction,
    variant = "floating",
    open,
    onClose,
    initialValues,
}: AddOperationCardProps) {
    const { t } = useTranslation();
    const { disabledIfOffline } = useOfflineDisabled();
    const mutationsDisabled = disabledIfOffline();
    const { quoteCurrencyView } = useMarketQuotesContext();
    const { balancesVisible } = useFinancialVisibility();
    const { profile } = useUserSession();

    const usernameForWallet = profile?.username ?? null;
    const { pools, loading: poolsLoading } = useWallet(usernameForWallet);

    const [internalIsFormOpen, setInternalIsFormOpen] = useState(false);
    const [side, setSide] = useState<"buy" | "sell" | "amortization">("buy");
    const [source, setSource] = useState<SourceType>("market");
    const [panel, setPanel] = useState<PortfolioPanelFilter>("acciones");
    const [selectedQuote, setSelectedQuote] = useState<FlattenedQuoteForAdd | null>(null);
    const [selectedCrypto, setSelectedCrypto] = useState<(typeof CRYPTO_OPTIONS)[number] | null>(null);
    const [customName, setCustomName] = useState("");
    const [customSymbol, setCustomSymbol] = useState("");
    const [quantity, setQuantity] = useState("");
    const [price, setPrice] = useState("");
    const [operationDate, setOperationDate] = useState("");
    const [operationCurrency, setOperationCurrency] = useState<string>(quoteCurrencyView);
    const filteredPoolsForOperation = useMemo(
        () => (operationCurrency ? pools.filter((p) => p.currency === operationCurrency) : pools),
        [pools, operationCurrency]
    );
    const draft = useFormDraft();
    const { state: draftState, actions: draftActions } = draft;
    const { submitting, error: submitError, discardPromptOpen: confirmDiscard } = draftState;
    const [selectedLiquidityPoolId, setSelectedLiquidityPoolId] = useState("");
    const [quantityTouched, setQuantityTouched] = useState(false);
    const [lastSellPrefillKey, setLastSellPrefillKey] = useState("");
    const isControlled = typeof open === "boolean";
    const isFormOpen = isControlled ? Boolean(open) : internalIsFormOpen;

    const setFormOpen = (nextOpen: boolean) => {
        if (!isControlled) setInternalIsFormOpen(nextOpen);
        if (!nextOpen) onClose?.();
    };

    const effectiveQuotesPanel = panel === "crypto" ? "acciones" : panel;
    const { data: quotesData, loading: quotesLoading } = useMarketQuotes(effectiveQuotesPanel);
    const flatQuotes = panel === "crypto" ? [] : flattenQuotesForAddDialog(quotesData?.quotes ?? [], effectiveQuotesPanel);

    const sortedQuotes = [...flatQuotes].sort((a, b) =>
        a.symbol.toLowerCase().localeCompare(b.symbol.toLowerCase())
    );

    const selectedAssetKey = useMemo(() => {
        if (source === "market") {
            if (panel === "crypto" && selectedCrypto) {
                return `market|crypto|${selectedCrypto.coingeckoId}`;
            }
            if (selectedQuote) {
                return `market|${panel}|${selectedQuote.symbol.toUpperCase()}`;
            }
            return "";
        }
        const symbol = customSymbol.trim().toUpperCase();
        const name = customName.trim().toUpperCase();
        if (symbol) return `custom|symbol|${symbol}`;
        if (name) return `custom|name|${name}`;
        return "";
    }, [source, panel, selectedQuote, selectedCrypto, customSymbol, customName]);

    const availableSellQuantity = useMemo(() => {
        if (!selectedAssetKey) return 0;
        const key = selectedAssetKey;
        return positions
            .filter((position) => {
                const asset = position.assetId;
                if (!asset) return false;
                if (key.startsWith("market|crypto|")) {
                    return (
                        asset.type === "market" &&
                        asset.marketPanel === "crypto" &&
                        asset.coingeckoId === key.replace("market|crypto|", "")
                    );
                }
                if (key.startsWith("market|")) {
                    const [, panelKey, symbolKey] = key.split("|");
                    return (
                        asset.type === "market" &&
                        asset.marketPanel === panelKey &&
                        asset.symbol?.toUpperCase() === symbolKey
                    );
                }
                if (key.startsWith("custom|symbol|")) {
                    const symbolKey = key.replace("custom|symbol|", "");
                    return asset.type === "custom" && asset.symbol?.toUpperCase() === symbolKey;
                }
                if (key.startsWith("custom|name|")) {
                    const nameKey = key.replace("custom|name|", "");
                    return asset.type === "custom" && asset.name?.toUpperCase() === nameKey;
                }
                return false;
            })
            .reduce((total, position) => total + (Number(position.quantity) || 0), 0);
    }, [positions, selectedAssetKey]);

    const availableSellQuantityText = useMemo(() => {
        const n = Number(availableSellQuantity.toFixed(4));
        return Number.isInteger(n) ? String(n) : n.toString();
    }, [availableSellQuantity]);

    useEffect(() => {
        if (side !== "sell") return;
        if (!selectedAssetKey) return;
        if (quantityTouched && lastSellPrefillKey === selectedAssetKey) return;
        const prefill = Number(availableSellQuantity.toFixed(4));
        setQuantity(prefill > 0 ? prefill.toString() : "");
        setQuantityTouched(false);
        setLastSellPrefillKey(selectedAssetKey);
    }, [side, selectedAssetKey, availableSellQuantity, quantityTouched, lastSellPrefillKey]);

    const handleQuantityChange = (value: string) => {
        draftActions.markChanged();
        setQuantity(value);
        setQuantityTouched(true);
    };

    const resetForm = () => {
        setSide("buy");
        setSource("market");
        setSelectedQuote(null);
        setSelectedCrypto(null);
        setCustomName("");
        setCustomSymbol("");
        setQuantity("");
        setPrice("");
        setOperationDate("");
        setOperationCurrency(quoteCurrencyView);
        draftActions.reset();
        setSelectedLiquidityPoolId("");
        setQuantityTouched(false);
        setLastSellPrefillKey("");
    };

    useEffect(() => {
        if (!isFormOpen || !initialValues) return;
        const nextSide = initialValues.side ?? "buy";
        const nextSource = initialValues.type ?? "market";
        const nextPanel = initialValues.marketPanel ?? "acciones";
        const nextSymbol = initialValues.symbol?.trim() ?? "";
        const nextName = initialValues.name?.trim() || nextSymbol;

        setSide(nextSide);
        setSource(nextSource);
        setPanel(nextPanel);
        setQuantity(initialValues.quantity != null ? String(initialValues.quantity) : "");
        setPrice(initialValues.price != null ? String(initialValues.price) : "");
        setOperationDate(initialValues.date ?? "");
        setOperationCurrency(initialValues.currency ?? quoteCurrencyView);
        draftActions.setError(null);
        setQuantityTouched(false);
        setLastSellPrefillKey("");
        draftActions.reset();

        if (nextSource === "market" && nextPanel !== "crypto" && nextSymbol) {
            setSelectedQuote({
                symbol: nextSymbol,
                name: nextName,
                defaultPrice: initialValues.price ?? null,
                panel: nextPanel,
            });
            setSelectedCrypto(null);
        } else if (nextSource === "custom") {
            setCustomName(nextName);
            setCustomSymbol(nextSymbol);
            setSelectedQuote(null);
            setSelectedCrypto(null);
        }
    }, [draftActions, initialValues, isFormOpen, quoteCurrencyView]);

    const requestClose = () => {
        const closeRequest = draftActions.requestClose();
        if (closeRequest !== "close") return;
        resetForm();
        setFormOpen(false);
    };

    const handleSubmit = async () => {
        if (!draftActions.beginSubmission()) return;
        const qty = Number(quantity);
        const pr = Number(price);
        if (isNaN(qty) || qty <= 0 || isNaN(pr) || pr < 0) {
            draftActions.endSubmission();
            draftActions.setError(t("portfolio.invalidQuantityPrice"));
            return;
        }
        if ((side === "sell" || side === "amortization") && qty > availableSellQuantity + 0.0001) {
            draftActions.endSubmission();
            draftActions.setError(t("portfolio.sellQuantityExceedsAvailable"));
            return;
        }

        if (source === "market") {
            if (panel === "crypto") {
                if (!selectedCrypto) {
                    draftActions.endSubmission();
                    draftActions.setError(t("portfolio.selectAsset"));
                    return;
                }
            } else if (!selectedQuote) {
                draftActions.endSubmission();
                draftActions.setError(t("portfolio.selectAsset"));
                return;
            }
        } else {
            if (!customName.trim()) {
                draftActions.endSubmission();
                draftActions.setError(t("portfolio.nameRequired"));
                return;
            }
        }

        draftActions.setError(null);

        try {
            const payload: AddOperationPayload =
                source === "market"
                    ? panel === "crypto"
                        ? {
                              type: "market",
                              symbol: selectedCrypto!.symbol,
                              name: selectedCrypto!.name,
                              marketPanel: "crypto",
                              coingeckoId: selectedCrypto!.coingeckoId,
                              side,
                              quantity: qty,
                              price: pr,
                              currency: operationCurrency,
                              date: operationDate.trim() || undefined,
                          }
                        : {
                              type: "market",
                              symbol: selectedQuote!.symbol,
                              name: selectedQuote!.name?.trim() || selectedQuote!.symbol,
                              marketPanel: panel,
                              side,
                              quantity: qty,
                              price: pr,
                              currency: operationCurrency,
                              date: operationDate.trim() || undefined,
                          }
                    : {
                          type: "custom",
                          name: customName.trim(),
                          symbol: customSymbol.trim() || undefined,
                          side,
                          quantity: qty,
                          price: pr,
                          currency: operationCurrency,
                          date: operationDate.trim() || undefined,
                      };

            const result = await addOperation(payload);
            if (result.success) {
                if (selectedLiquidityPoolId) {
                    const sync = await onCreateWalletTransaction(payload, selectedLiquidityPoolId);
                    if (!sync.success) {
                        toast.error(
                            t("portfolio.walletSyncErrorDetailed", {
                                details: sync.error || t("portfolio.walletSyncError"),
                            })
                        );
                    }
                }
                onSuccess();
                resetForm();
                setFormOpen(false);
            } else {
                draftActions.setError(result.error ?? t("portfolio.addOperationError"));
            }
        } catch (err) {
            draftActions.setError(String(err));
        } finally {
            draftActions.endSubmission();
        }
    };

    const baseCanSubmit =
        source === "market"
            ? panel === "crypto"
                ? Boolean(selectedCrypto && quantity && price && Number(quantity) > 0 && Number(price) >= 0)
                : Boolean(selectedQuote && quantity && price && Number(quantity) > 0 && Number(price) >= 0)
            : Boolean(customName.trim() && quantity && price && Number(quantity) > 0 && Number(price) >= 0);
    const canSubmit = baseCanSubmit && ((side !== "sell" && side !== "amortization") || Number(quantity) <= availableSellQuantity + 0.0001);

    const sectionSx = {
        bgcolor: "action.hover",
        borderRadius: 2,
        p: 1.5,
    };

    const sectionLabelSx = {
        fontWeight: 600,
        display: "block",
        mb: 0.5,
        letterSpacing: 0.3,
    };

    const compactTabsSx = {
        minHeight: 36,
        "& .MuiTab-root": { minHeight: 36, py: 0.5, textTransform: "none", fontSize: "0.8rem" },
    };

    const isAmortizationOnly = isFormOpen && initialValues?.side === "amortization";

    const content = (
        <CardContent>
            <Stack spacing={FORM_FIELD_GAP}>
                {/* --- Side & Source --- */}
                <Box sx={sectionSx}>
                    {isAmortizationOnly ? (
                        <Typography variant="caption" color="text.secondary" sx={sectionLabelSx}>
                            {t("portfolio.amortization")}
                        </Typography>
                    ) : (
                        <>
                            <Typography variant="caption" color="text.secondary" sx={sectionLabelSx}>
                                {t("portfolio.operationSide")}
                            </Typography>
                            <Tabs
                                value={side}
                                onChange={(_, v) => {
                                    draftActions.markChanged();
                                    setSide(v as "buy" | "sell" | "amortization");
                                    draftActions.setError(null);
                                    setQuantityTouched(false);
                                    setLastSellPrefillKey("");
                                }}
                                variant="fullWidth"
                                sx={compactTabsSx}
                            >
                                <Tab value="buy" label={t("portfolio.buy")} />
                                <Tab value="sell" label={t("portfolio.sell")} />
                            </Tabs>
                        </>
                    )}
                    <Divider sx={{ my: 1.5, borderStyle: "dashed" }} />
                    <Typography variant="caption" color="text.secondary" sx={sectionLabelSx}>
                        {t("portfolio.positionSource")}
                    </Typography>
                    <Tabs
                        value={source}
                        onChange={(_, v) => {
                            draftActions.markChanged();
                            setSource(v as SourceType);
                            setSelectedQuote(null);
                            setSelectedCrypto(null);
                            draftActions.setError(null);
                            setQuantityTouched(false);
                            setLastSellPrefillKey("");
                        }}
                        variant="fullWidth"
                        sx={compactTabsSx}
                    >
                        <Tab value="market" label={t("portfolio.fromMarket")} />
                        <Tab value="custom" label={t("portfolio.custom")} />
                    </Tabs>
                </Box>

                {/* --- Asset selection --- */}
                {source === "market" && (
                    <Box sx={sectionSx}>
                        <Typography variant="caption" color="text.secondary" sx={sectionLabelSx}>
                            {t("portfolio.searchTicker")}
                        </Typography>
                        <Tabs
                            value={panel}
                            onChange={(_, v) => {
                                draftActions.markChanged();
                                setPanel(v as PortfolioPanelFilter);
                                setSelectedQuote(null);
                                setSelectedCrypto(null);
                                setPrice("");
                                setQuantityTouched(false);
                                setLastSellPrefillKey("");
                            }}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{ ...compactTabsSx, "& .MuiTab-root": { ...compactTabsSx["& .MuiTab-root"], fontSize: "0.75rem" } }}
                        >
                            {PORTFOLIO_PANEL_FILTERS.map((p) => (
                                <Tab key={p} value={p} label={t(`portfolio.${p}`)} />
                            ))}
                        </Tabs>
                        {panel === "crypto" ? (
                            <Autocomplete
                                options={CRYPTO_OPTIONS}
                                getOptionLabel={(option) => option.symbol}
                                value={selectedCrypto}
                                onChange={(_, newValue) => {
                                    draftActions.markChanged();
                                    setSelectedCrypto(newValue);
                                    setPrice("");
                                    setQuantityTouched(false);
                                    setLastSellPrefillKey("");
                                }}
                                isOptionEqualToValue={(option, value) => option.coingeckoId === value?.coingeckoId}
                                noOptionsText={t("portfolio.noTickersMatch")}
                                filterOptions={(options, { inputValue }) => {
                                    const lower = inputValue.toLowerCase();
                                    return options.filter(
                                        (q) =>
                                            q.symbol.toLowerCase().includes(lower) ||
                                            q.name.toLowerCase().includes(lower) ||
                                            q.coingeckoId.toLowerCase().includes(lower)
                                    );
                                }}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.coingeckoId}>
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{option.symbol}</Typography>
                                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: { xs: "none", sm: "block" } }}>
                                                {option.name}
                                            </Typography>
                                        </Box>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder={t("portfolio.searchTicker")}
                                        size="small"
                                        sx={{ mt: 1 }}
                                    />
                                )}
                                fullWidth
                                size="small"
                            />
                        ) : (
                            <Autocomplete
                                options={sortedQuotes}
                                getOptionLabel={(option) => option.symbol}
                                value={selectedQuote}
                                onChange={(_, newValue) => {
                                    draftActions.markChanged();
                                    setSelectedQuote(newValue);
                                    setPrice(newValue?.defaultPrice ? String(newValue.defaultPrice) : "");
                                    setQuantityTouched(false);
                                    setLastSellPrefillKey("");
                                }}
                                isOptionEqualToValue={(option, value) => option.symbol === value.symbol}
                                loading={quotesLoading}
                                loadingText={t("portfolio.loading")}
                                noOptionsText={t("portfolio.noTickersMatch")}
                                filterOptions={(options, { inputValue }) => {
                                    const lower = inputValue.toLowerCase();
                                    return options.filter(
                                        (q) =>
                                            q.symbol.toLowerCase().includes(lower) ||
                                            q.name.toLowerCase().includes(lower)
                                    );
                                }}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.symbol}>
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{option.symbol}</Typography>
                                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: { xs: "none", sm: "block" } }}>
                                                {option.name}
                                            </Typography>
                                        </Box>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder={t("portfolio.searchTicker")}
                                        size="small"
                                        sx={{ mt: 1 }}
                                    />
                                )}
                                fullWidth
                                size="small"
                            />
                        )}
                    </Box>
                )}

                {source === "custom" && (
                    <Box sx={sectionSx}>
                        <Typography variant="caption" color="text.secondary" sx={sectionLabelSx}>
                            {t("portfolio.custom")}
                        </Typography>
                        <Stack spacing={1.5}>
                            <TextField
                                label={t("portfolio.customName")}
                                value={customName}
                                onChange={(e) => {
                            draftActions.markChanged();
                                    setCustomName(e.target.value);
                                    setQuantityTouched(false);
                                    setLastSellPrefillKey("");
                                }}
                                fullWidth
                                size="small"
                                required
                            />
                            <TextField
                                label={t("portfolio.customSymbol")}
                                value={customSymbol}
                                onChange={(e) => {
                            draftActions.markChanged();
                                    setCustomSymbol(e.target.value);
                                    setQuantityTouched(false);
                                    setLastSellPrefillKey("");
                                }}
                                fullWidth
                                size="small"
                                placeholder={t("portfolio.optional")}
                            />
                        </Stack>
                    </Box>
                )}

                {/* --- Details --- */}
                <Box sx={sectionSx}>
                    <Typography variant="caption" color="text.secondary" sx={{ ...sectionLabelSx, mb: 1 }}>
                        {t("portfolio.details")}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                        <MoneyInput
                            label={t("portfolio.quantity")}
                            value={quantity}
                            onValueChange={handleQuantityChange}
                            size="small"
                            decimals={4}
                            sx={{ flex: 1, minWidth: { xs: "100%", sm: 100 } }}
                        />
                        <MoneyInput
                            label={t("portfolio.price")}
                            value={price}
                            onValueChange={(value) => { draftActions.markChanged(); setPrice(value); }}
                            size="small"
                            sx={{ flex: 1, minWidth: { xs: "100%", sm: 100 } }}
                        />
                        <TextField
                            label={t("portfolio.operationDate")}
                            type="date"
                            value={operationDate}
                            onChange={(e) => { draftActions.markChanged(); setOperationDate(e.target.value); }}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            sx={{ flex: 1, minWidth: { xs: "100%", sm: 130 } }}
                        />
                    </Box>
                    {Number(quantity) > 0 && Number(price) > 0 && (
                        <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                            {t("portfolio.operationTotal")}: {formatCurrencyAmountOrMasked(Number(quantity) * Number(price), operationCurrency, balancesVisible)}
                        </Typography>
                    )}
                    {(side === "sell" || side === "amortization") && (
                        <Typography
                            variant="body2"
                            sx={{ mt: 1, color: Number(quantity) > availableSellQuantity + 0.0001 ? "error.main" : "text.secondary" }}
                        >
                            {t("portfolio.sellAvailableQuantity", { quantity: availableSellQuantityText })}
                        </Typography>
                    )}
                </Box>

                {(side === "buy" || side === "sell" || side === "amortization") && (
                    <Box sx={sectionSx}>
                        {!poolsLoading && filteredPoolsForOperation.length === 0 ? (
                            <WalletEmptyHint />
                        ) : (
                            <>
                                <Typography variant="caption" color="text.secondary" sx={{ ...sectionLabelSx, mb: 1 }}>
                                    {t("portfolio.walletImpact")}
                                </Typography>
                                <LiquidityPoolSelector
                                    value={selectedLiquidityPoolId}
                                    onChange={(value) => { draftActions.markChanged(); setSelectedLiquidityPoolId(value); }}
                                    currencyFilter={operationCurrency}
                                    size="small"
                                />
                            </>
                        )}
                    </Box>
                )}

                {submitError && (
                    <Typography color="error" variant="body2">
                        {submitError}
                    </Typography>
                )}

                <FormActions
                    submitLabel={isAmortizationOnly ? t("portfolio.recordAmortization") : t("portfolio.createOperation")}
                    submitButtonType="button"
                    onSubmit={handleSubmit}
                    onCancel={requestClose}
                    submitDisabled={!canSubmit || mutationsDisabled}
                    submitLoading={submitting}
                />
            </Stack>
        </CardContent>
    );

    if (variant === "dialog") {
        return (
            <>
                <FormDialog
                    open={isFormOpen}
                    onClose={requestClose}
                    title={isAmortizationOnly ? t("portfolio.recordAmortization") : t("portfolio.addOperation")}
                    subtitle={t("portfolio.operationDialogSubtitle")}
                    disabled={submitting}
                >
                    {content}
                </FormDialog>
                <ConfirmDialog
                    open={confirmDiscard}
                    title={t("forms.discardChangesTitle")}
                    description={t("forms.discardChangesDescription")}
                    cancelLabel={t("forms.keepEditing")}
                    confirmLabel={t("forms.discardDraft")}
                    confirmColor="error"
                    onCancel={draftActions.dismissDiscard}
                    onConfirm={() => {
                        draftActions.dismissDiscard();
                        resetForm();
                        setFormOpen(false);
                    }}
                />
            </>
        );
    }

    return (
        <DialogCard title={t("portfolio.addOperation")} triggerLabel={t("portfolio.addOperation")} disabled={mutationsDisabled} open={isFormOpen} onOpenChange={setFormOpen} onRequestClose={requestClose}>{content}</DialogCard>
    );
}
